import { TFilterValues } from '@components/table/types';
import { Grid, Chip as MuiChip, Stack, Box } from '@mui/material';
import { selectedChipSx } from '@theme/styleOverrides/chip';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type TQuickFilter = {
  defaultChecked?: boolean;
  value: string;
  hidden?: boolean;
  label: string;
  count?: number;
};

export type QuickFiltersProps = {
  quickFilterGroup: { queryKey: string; quickFilters: TQuickFilter[] }[];
  onChange: (quickFilters: TFilterValues) => void;
  defaultFilters?: TFilterValues;
};

export const QuickFilters = ({ onChange, defaultFilters, quickFilterGroup }: QuickFiltersProps) => {
  const [selectedFilters, setSelectedFilters] = useState<TFilterValues>(defaultFilters ?? {});
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();

  const handleApplyFilterByStatus = (queryKey: string, qf: TQuickFilter) => {
    const filterGroup = quickFilterGroup.find((x) => x.queryKey === queryKey);
    let newValue: TQuickFilter['value'] | null = qf.value;

    if (filterGroup?.quickFilters.length === 1) {
      const currentValue = selectedFilters[queryKey];
      if (currentValue === qf.value) {
        newValue = null; // toggle off if already selected}
      }
    }

    const newFilters = { ...selectedFilters, [queryKey]: newValue };
    setSelectedFilters({ ...newFilters });

    if (newValue === null) {
      searchParamsInTheUrl.delete(queryKey);
    } else {
      searchParamsInTheUrl.set(queryKey, qf.value);
    }
    setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
    onChange(newFilters);
  };

  useEffect(() => {
    // determine if any of the selectedFilters are different from the searchParamsInTheUrl
    // this will happen if 'view all records/reset filters' is clicked
    const newFilters: TFilterValues = {};
    let quickFiltersChanged = false;
    quickFilterGroup.forEach((qfp) => {
      const filterValue = searchParamsInTheUrl.get(qfp.queryKey);
      // if the qfp has only one quick filter, we don't want to change the selectedFilters
      if (qfp?.quickFilters.length !== 1 && filterValue !== selectedFilters[qfp.queryKey]) {
        newFilters[qfp.queryKey] = filterValue;
        quickFiltersChanged = true;
      }
    });
    if (quickFiltersChanged) {
      setSelectedFilters((prev) => ({ ...prev, ...newFilters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsInTheUrl]);

  return (
    <Grid size={{ xs: 12 }} sx={{ rowGap: 1 }} container>
      {quickFilterGroup?.map((qfp) => {
        return (
          <Stack
            key={qfp.queryKey}
            direction='row'
            spacing='8px'
            useFlexGap
            sx={{
              flexWrap: 'wrap',
              alignItems: 'center',
              '&:not(:last-child)': {
                borderRightWidth: '1px',
                borderRightColor: 'border.main',
                borderRightStyle: 'solid',
                paddingRight: 1,
                marginRight: 1,
              },
            }}
          >
            {qfp.quickFilters
              ?.filter((x) => x.hidden !== true)
              .map((filter) => {
                return (
                  <MuiChip
                    size='small'
                    key={filter.value}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {filter.label}
                        {filter.count !== undefined && filter.count > 0 && (
                          <>
                            {' '}
                            -{' '}
                            <Box
                              sx={{
                                ml: 0.5,
                                fontSize: '11px',
                                color: selectedFilters?.[qfp.queryKey] === filter.value ? 'inherit' : 'error.light',
                              }}
                            >
                              {filter.count > 99 ? '99+' : filter.count}
                            </Box>
                          </>
                        )}
                      </Box>
                    }
                    onClick={() => handleApplyFilterByStatus(qfp.queryKey, filter)}
                    variant={selectedFilters?.[qfp.queryKey] === filter.value ? 'filled' : 'outlined'}
                    sx={
                      selectedFilters?.[qfp.queryKey] === filter.value
                        ? {
                            borderColor: 'border.light',
                            borderStyle: 'solid',
                            borderWidth: '1px',
                            ...selectedChipSx,
                          }
                        : {}
                    }
                  />
                );
              })}
          </Stack>
        );
      })}
    </Grid>
  );
};
